import { syncQueue } from '../offline/syncQueue';
import { offlineStorage } from '../offline/offlineStorage';

export type SyncStrategy = 'full' | 'data-only' | 'incremental';

export interface SyncOptions {
  strategy?: SyncStrategy;
  priority?: 'high' | 'normal' | 'low';
  includeImages?: boolean;
  batchSize?: number;
}

export interface SyncProgress {
  total: number;
  completed: number;
  failed: number;
  currentItem?: string;
  percentage: number;
}

export interface SyncResult {
  success: boolean;
  synced: number;
  failed: number;
  duration: number;
  errors?: string[];
}

class DataSyncService {
  private isSyncing = false;
  private lastSyncTime: Date | null = null;
  private syncListeners: ((progress: SyncProgress) => void)[] = [];
  private completionListeners: ((result: SyncResult) => void)[] = [];

  constructor() {
    this.setupAutoSync();
  }

  private setupAutoSync() {
    // Auto-sync on network restoration
    window.addEventListener('online', () => {
      console.log('DataSync: Network restored, starting auto-sync...');
      this.sync({ strategy: 'incremental' });
    });

    // Note: Background sync requires service worker setup
    // Uncomment when PWA/service worker is implemented
    // this.setupBackgroundSync();
  }

  private async setupBackgroundSync() {
    try {
      const registration = await navigator.serviceWorker.ready;
      await (registration as any).sync.register('data-sync');
      console.log('DataSync: Background sync registered');
    } catch (error) {
      console.error('DataSync: Background sync registration failed', error);
    }
  }

  onProgress(callback: (progress: SyncProgress) => void) {
    this.syncListeners.push(callback);
    return () => {
      this.syncListeners = this.syncListeners.filter(cb => cb !== callback);
    };
  }

  onComplete(callback: (result: SyncResult) => void) {
    this.completionListeners.push(callback);
    return () => {
      this.completionListeners = this.completionListeners.filter(cb => cb !== callback);
    };
  }

  private notifyProgress(progress: SyncProgress) {
    this.syncListeners.forEach(listener => listener(progress));
  }

  private notifyCompletion(result: SyncResult) {
    this.completionListeners.forEach(listener => listener(result));
  }

  async sync(options: SyncOptions = {}): Promise<SyncResult> {
    if (this.isSyncing) {
      console.log('DataSync: Sync already in progress');
      return {
        success: false,
        synced: 0,
        failed: 0,
        duration: 0,
        errors: ['Sync already in progress'],
      };
    }

    if (!navigator.onLine) {
      console.log('DataSync: Cannot sync while offline');
      return {
        success: false,
        synced: 0,
        failed: 0,
        duration: 0,
        errors: ['Device is offline'],
      };
    }

    this.isSyncing = true;
    const startTime = Date.now();
    let synced = 0;
    let failed = 0;
    const errors: string[] = [];

    try {
      const strategy = options.strategy || this.determineStrategy();
      console.log(`DataSync: Starting ${strategy} sync`);

      // Get pending operations from sync queue
      const queue = syncQueue.getQueue();
      const pendingOps = queue.filter(op => op.status === 'pending' || op.status === 'failed');
      
      const total = pendingOps.length;
      let completed = 0;

      // Filter operations based on strategy
      let opsToSync = pendingOps;
      if (strategy === 'data-only' || !options.includeImages) {
        opsToSync = pendingOps.filter(op => op.resource !== 'photo');
      }

      // Process in batches
      const batchSize = options.batchSize || 5;
      
      for (let i = 0; i < opsToSync.length; i += batchSize) {
        const batch = opsToSync.slice(i, i + batchSize);
        
        // Notify progress
        this.notifyProgress({
          total,
          completed,
          failed,
          currentItem: batch[0]?.resource,
          percentage: (completed / total) * 100,
        });

        // Process batch
        try {
          await syncQueue.processBatch(batch.length);
          synced += batch.length;
          completed += batch.length;
        } catch (error: unknown) {
          failed += batch.length;
          completed += batch.length;
          errors.push(error instanceof Error ? error.message : String(error));
        }
      }

      // Incremental sync: only sync changed data
      if (strategy === 'incremental') {
        await this.syncIncrementalData();
      }

      this.lastSyncTime = new Date();
      
      const result: SyncResult = {
        success: failed === 0,
        synced,
        failed,
        duration: Date.now() - startTime,
        errors: errors.length > 0 ? errors : undefined,
      };

      this.notifyCompletion(result);
      
      return result;
    } catch (error: unknown) {
      console.error('DataSync: Sync failed', error);

      const result: SyncResult = {
        success: false,
        synced,
        failed: failed + 1,
        duration: Date.now() - startTime,
        errors: [...errors, error instanceof Error ? error.message : String(error)],
      };

      this.notifyCompletion(result);
      
      return result;
    } finally {
      this.isSyncing = false;
    }
  }

  private determineStrategy(): SyncStrategy {
    // Check Network Information API
    const connection = (navigator as any).connection || 
                       (navigator as any).mozConnection || 
                       (navigator as any).webkitConnection;

    if (!connection) return 'incremental';

    const effectiveType = connection.effectiveType;
    const downlink = connection.downlink;

    // Fast connection: do full sync with images
    if (effectiveType === '4g' && downlink > 5) {
      return 'full';
    }

    // Slow connection: data only
    if (effectiveType === '2g' || downlink < 1) {
      return 'data-only';
    }

    // Default: incremental sync
    return 'incremental';
  }

  private async syncIncrementalData(): Promise<void> {
    // Only sync data that has changed since last sync
    if (!this.lastSyncTime) return;

    try {
      // Get unsynced activities
      const activities = await offlineStorage.getUnsyncedActivities();
      console.log(`DataSync: Syncing ${activities.length} unsynced activities`);

      // Get unsynced photos
      const photos = await offlineStorage.getUnsyncedPhotos();
      console.log(`DataSync: Syncing ${photos.length} unsynced photos`);

      // Get unsynced voice recordings
      const voiceRecordings = await offlineStorage.getUnsyncedVoiceRecordings();
      console.log(`DataSync: Syncing ${voiceRecordings.length} unsynced voice recordings`);

      // Process each type
      // In production, these would make actual API calls
    } catch (error) {
      console.error('DataSync: Incremental sync failed', error);
    }
  }

  getLastSyncTime(): Date | null {
    return this.lastSyncTime;
  }

  isSyncInProgress(): boolean {
    return this.isSyncing;
  }

  async forceFullSync(): Promise<SyncResult> {
    return this.sync({ 
      strategy: 'full', 
      includeImages: true,
      priority: 'high' 
    });
  }

  async syncDataOnly(): Promise<SyncResult> {
    return this.sync({ 
      strategy: 'data-only', 
      includeImages: false,
      priority: 'normal' 
    });
  }
}

export const dataSync = new DataSyncService();
